import './TheBizInfo.css'; // Import the CSS
import htmlContent from './TheBizInfo.html?raw'; // Import HTML as a raw string

class TheBizInfo {
  constructor(container) {
    this.container = container;
    this.dropdownsData = {
      'type-a': { options: ['Apps', 'Websites', 'Extensions', 'Components', 'Content'], defaultText: 'Apps' },
      'type-b': { options: ['Creators', 'Businesses', 'Everyday people', 'Influencers', 'Artists', 'Writers', 'Developers'], defaultText: 'Creators' },
    };
  }

  async render() {
    try {
      // const response = await fetch('./src/components/Content/TheBizInfo.html');
      // const htmlContent = await response.text();
      this.container.innerHTML = htmlContent; // Use the imported string directly
      this.setupEventListeners();
      this.initDropdowns();
      
      // Add a subtle load animation
      this.container.style.opacity = '0';
      this.container.style.transform = 'translateY(10px)';
      this.container.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
      
      setTimeout(() => {
        this.container.style.opacity = '1';
        this.container.style.transform = 'translateY(0)';
      }, 50);
      
    } catch (error) {
      console.error('Error rendering TheBizInfo component:', error);
      this.container.innerHTML = '<p>Error loading content. Please refresh the page.</p>';
    }
  }

  setupEventListeners() {
    const expandableTrigger = this.container.querySelector('.the-biz-expandable-trigger');
    const expandableContent = this.container.querySelector('.the-biz-expandable-content');
    const expandableTextSpan = this.container.querySelector('.the-biz-expandable-text');

    if (expandableTrigger && expandableContent && expandableTextSpan) {
      expandableTrigger.addEventListener('click', () => {
        const isOpen = expandableTrigger.classList.toggle('open');
        expandableContent.classList.toggle('open');
        expandableTextSpan.textContent = isOpen ? 'Say Less' : 'Say More';
        
        // Enhance accessibility
        expandableTrigger.setAttribute('aria-expanded', isOpen);
        expandableContent.setAttribute('aria-hidden', !isOpen);
        
        // Remove focus after interaction to prevent persistent outline
        expandableTrigger.blur();
      });

      // Add keyboard accessibility
      expandableTrigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          expandableTrigger.click();
        }
      });

      // Make expandable trigger focusable
      expandableTrigger.setAttribute('tabindex', '0');
      expandableTrigger.setAttribute('role', 'button');
      expandableTrigger.setAttribute('aria-expanded', 'false');
      expandableContent.setAttribute('aria-hidden', 'true');
    }
  }
  
  initDropdowns() {
    const dropdownElements = this.container.querySelectorAll('.the-biz-dropdown');
    dropdownElements.forEach(el => {
      // Set initial text from defaultText attribute or data
      const dropdownId = el.dataset.dropdownFor;
      const defaultText = el.dataset.defaultText || (this.dropdownsData[dropdownId] ? this.dropdownsData[dropdownId].defaultText : 'Select');
      el.innerHTML = `${defaultText} <span class="the-biz-dropdown-caret">▼</span>`;

      // Enhanced accessibility
      el.setAttribute('role', 'combobox');
      el.setAttribute('aria-expanded', 'false');
      el.setAttribute('aria-haspopup', 'listbox');
      el.setAttribute('tabindex', '0');

      el.addEventListener('click', (event) => {
        event.stopPropagation(); 
        this.toggleDropdown(el);
      });

      // Add keyboard navigation
      el.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleDropdown(el);
        } else if (event.key === 'Escape') {
          this.closeDropdown(el);
        }
      });
    });
  }

  closeDropdown(element) {
    const existingMenu = element.querySelector('.the-biz-dropdown-menu');
    const caret = element.querySelector('.the-biz-dropdown-caret');
    
    if (existingMenu) {
      existingMenu.remove();
      if (caret) caret.textContent = '▼';
      element.setAttribute('aria-expanded', 'false');
    }
  }

  toggleDropdown(element) {
    const dropdownId = element.dataset.dropdownFor;
    const existingMenu = element.querySelector('.the-biz-dropdown-menu');
    const caret = element.querySelector('.the-biz-dropdown-caret');

    // Close all other dropdowns
    this.container.querySelectorAll('.the-biz-dropdown').forEach(dropdown => {
      if (dropdown !== element) {
        this.closeDropdown(dropdown);
      }
    });

    if (existingMenu) {
      this.closeDropdown(element);
      return;
    }

    const dropdownData = this.dropdownsData[dropdownId];
    if (!dropdownData || !dropdownData.options) return;
    if (caret) caret.textContent = '▲'; // Change caret to up when open
    element.setAttribute('aria-expanded', 'true');

    const menu = document.createElement('ul');
    menu.className = 'the-biz-dropdown-menu';
    menu.setAttribute('role', 'listbox');
    
    dropdownData.options.forEach((optionText, index) => {
      const listItem = document.createElement('li');
      listItem.textContent = optionText;
      listItem.setAttribute('role', 'option');
      listItem.setAttribute('tabindex', '-1');
      
      listItem.addEventListener('click', (e) => {
        element.firstChild.textContent = optionText + ' '; // Update display text, keep space for caret
        this.closeDropdown(element);
        element.blur(); // Remove focus completely after selection
      });

      // Add keyboard navigation for dropdown items
      listItem.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          listItem.click();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextItem = listItem.nextElementSibling;
          if (nextItem) nextItem.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevItem = listItem.previousElementSibling;
          if (prevItem) prevItem.focus();
        } else if (e.key === 'Escape') {
          this.closeDropdown(element);
          element.blur();
        }
      });

      menu.appendChild(listItem);
    });

    element.appendChild(menu);
    menu.style.position = 'absolute';
    menu.style.backgroundColor = 'var(--the-biz-dropdown-bg)';
    menu.style.border = '1px solid var(--the-biz-dropdown-border)';
    menu.style.listStyle = 'none';
    menu.style.padding = '5px';
    menu.style.margin = '5px 0 0 0';
    menu.style.zIndex = '1000'; // Increased z-index
    menu.style.minWidth = element.offsetWidth + 'px';
    menu.style.maxHeight = '150px';
    menu.style.overflowY = 'auto';
    menu.style.borderRadius = '3px';
    menu.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';

    // Focus first item for keyboard navigation
    const firstItem = menu.querySelector('li');
    if (firstItem) {
      firstItem.setAttribute('tabindex', '0');
    }

    const clickOutsideHandler = (event) => {
      if (!menu.contains(event.target) && event.target !== element && !element.contains(event.target)) {
        this.closeDropdown(element);
        element.blur(); // Remove focus when clicking outside
        document.removeEventListener('click', clickOutsideHandler);
      }
    };
    // Add timeout to allow current click event to finish before attaching new one
    setTimeout(() => document.addEventListener('click', clickOutsideHandler), 0);
  }
}

export default TheBizInfo; 